import { getBytes, getLines, getMessages } from './sse-parse';

/**
 * SSE 错误基类
 */
export class SseError extends Error {
  constructor(
    message: string,
    public readonly resource: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'SseError';
  }
}

/**
 * SSE HTTP 错误 - 包含状态码，方便外部判断（如 401 需要重连）
 */
export class SseHttpError extends SseError {
  constructor(
    public readonly resource: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly body?: string,
  ) {
    super(`HTTP ${status} ${statusText}`, resource);
    this.name = 'SseHttpError';
  }
}

/**
 * 类型守卫：判断是否为 HTTP 错误，主要是给401的情况使用，业务层要走重连的逻辑
 */
export function isSseHttpError(error: unknown): error is SseHttpError {
  return error instanceof SseHttpError;
}

export async function* streamServerSentEvents(
  resource: string,
  options?: RequestInit,
): AsyncGenerator<string> {
  let resp: Response;

  // 1. fetch 失败
  try {
    resp = await fetch(resource, { ...options });
  } catch (error) {
    console.error('[SSE] fetch failed', { resource, error });
    throw new SseError('Fetch failed', resource, error);
  }

  // 2. HTTP 非 2xx（包含 401 等需要外部特殊处理的状态码）
  if (!resp.ok) {
    let bodyText: string | undefined;
    try {
      bodyText = await resp.text();
    } catch {}

    console.error('[SSE] HTTP error', {
      resource,
      status: resp.status,
      statusText: resp.statusText,
      body: bodyText,
    });
    throw new SseHttpError(resource, resp.status, resp.statusText, bodyText);
  }

  // 3. 响应体为空
  if (!resp.body) {
    console.error('[SSE] response body is null', { resource });
    throw new SseError('Response body is null', resource);
  }

  const messages: string[] = [];
  let resolve: (() => void) | null = null;

  const processMessages = getBytes(
    resp.body,
    getLines(
      getMessages(
        () => {},
        () => {},
        (message: string) => {
          messages.push(message);
          if (resolve) {
            resolve();
            resolve = null;
          }
        },
      ),
    ),
  );

  // 4. 流解析错误
  try {
    try {
      while (true) {
        while (messages.length > 0) {
          yield messages.shift()!;
        }

        await Promise.race([
          processMessages,
          new Promise<void>((r) => {
            resolve = r;
          }),
        ]);

        if (messages.length === 0) {
          break;
        }
      }
    } catch (error) {
      console.error('[SSE] stream/parse error', { resource, error });
      throw new SseError('Stream/parse error', resource, error);
    }
  } finally {
    await processMessages.catch((error) => {
      console.error('[SSE] processMessages error', { resource, error });
    });
  }
}
